'use client';

import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';
import type { MatchWithRatingsV2 } from './types';
import { LocalDateTime } from '@/components/ui/LocalDateTime';
import { formatDate } from '@/lib/utils';
import { TableRow, TableCell, Link, Button } from '@/components/ui';
import PlayerCard from '@/components/player/PlayerCard';
import { apiRequest } from '@/lib/api';

interface InProgressMatchRowProps {
  match: MatchWithRatingsV2;
  onDelete?: (matchId: string) => void;
}

export function InProgressMatchRow({ match, onDelete }: InProgressMatchRowProps) {
  const {
    id,
    dateISO,
    player1,
    player2,
    venue,
    competition,
    competitionDivision,
    isMarkedByPlayer: _isMarkedByPlayer,
  } = match;

  const locale = useLocale();
  const t = useTranslations('Matches');
  const [isAbandoning, setIsAbandoning] = useState(false);

  const handleAbandon = async () => {
    if (!onDelete) return;

    const confirmMsg = t('confirmAbandonMatch');
    if (!window.confirm(confirmMsg)) return;

    setIsAbandoning(true);
    try {
      await apiRequest(`/api/match/${id}/abandon`, { method: 'POST' });
      onDelete(id);
    } catch (error) {
      // 404: already gone (abandoned elsewhere) - the row should go too.
      // The route also answers 404 for a forbidden caller, by design (opaque).
      if ((error as { status?: number }).status === 404) {
        onDelete(id);
        return;
      }
      console.error('Error abandoning match:', error);
      alert(t('errorAbandoningMatch'));
    } finally {
      setIsAbandoning(false);
    }
  };

  return (
    <TableRow className="border-l-primary bg-primary/5 border-l-4">
      <TableCell className="text-xs md:text-sm">
        <div>
          {/* `dateISO` is a timestamptz — see MatchCard. */}
          <LocalDateTime iso={dateISO} locale={locale} format={formatDate} className="block" />
          <div className="text-primary flex items-center gap-1 text-xs font-medium">
            <div className="bg-primary h-1.5 w-1.5 animate-pulse rounded-full"></div>
            {t('inProgress')}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className="space-y-1">
          {player1 && (
            <PlayerCard
              player={{
                id: player1.id,
                username: player1.username,
                first_name: player1.first_name,
                last_name: player1.last_name,
                profile_photo: player1.profile_photo,
                current_rating: player1.current_rating,
              }}
              showRating={false}
              showUsername={true}
              size="compact"
              clickable={true}
              className="md:h-8"
            />
          )}
          {player2 && (
            <PlayerCard
              player={{
                id: player2.id,
                username: player2.username,
                first_name: player2.first_name,
                last_name: player2.last_name,
                profile_photo: player2.profile_photo,
                current_rating: player2.current_rating,
              }}
              showRating={false}
              showUsername={true}
              size="compact"
              clickable={true}
              className="md:h-8"
            />
          )}
        </div>
      </TableCell>
      {/* Hide competition on smaller screens */}
      <TableCell className="hidden lg:table-cell">
        <div className="text-xs md:text-sm">
          {competition === 'Ranking Adjustment' ? t('rankingAdjustment') : competition || '—'}
          {competitionDivision && <span className="text-muted ml-1">• {competitionDivision}</span>}
        </div>
      </TableCell>
      <TableCell>
        <div className="text-primary text-xs font-medium md:text-sm">{t('inProgress')}</div>
      </TableCell>
      <TableCell className="hidden md:table-cell">
        <div className="text-xs md:text-sm">{venue || '—'}</div>
      </TableCell>
      <TableCell className="text-right">
        <div className="text-foreground text-xs md:text-sm">—</div>
      </TableCell>
      <TableCell className="hidden text-right sm:table-cell">
        <div className="text-foreground text-xs md:text-sm">—</div>
      </TableCell>
      <TableCell className="hidden text-center sm:table-cell">
        <div className="flex flex-col items-center gap-2">
          <Link href={`/match/score/${id}`} variant="filled" color="primary" size="sm">
            {t('continueScoring')}
          </Link>
          {onDelete && (
            <Button
              variant="outline"
              color="destructive"
              size="sm"
              onClick={handleAbandon}
              disabled={isAbandoning}
            >
              {isAbandoning ? t('abandoning') : t('abandon')}
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
